/**
 * Pricing Page Component
 * 
 * Display pricing plans with monthly/yearly toggle.
 * Public page (no authentication required, but can fetch plans if authenticated).
 */

import React, { useState, useEffect } from "react";
import { PublicLayout } from "../components/public/PublicLayout";
import { getPlans, type Plan } from "../services/billingApi";
import { getQueryParam, preserveQueryParams } from "../utils/urlParams";
import { useAuth } from "../hooks/useAuth";
import { logger } from "../services/logger";
import "./pricing-page.css";

type BillingCycle = "monthly" | "yearly";

// Stub data for public access (when not authenticated)
const STUB_PLANS: Plan[] = [
  {
    id: "stub-standard",
    code: "STANDARD",
    name: "Pro",
    prices: {
      monthly: 59,
      yearly: 590,
    },
    limits: {
      daily_trade_cap: 0,
    },
  },
  {
    id: "stub-pro",
    code: "PRO",
    name: "Ultra",
    prices: {
      monthly: 119,
      yearly: 1190,
    },
    limits: {
      daily_trade_cap: 0,
    },
  },
];

export function PricingPage(): JSX.Element {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [plans, setPlans] = useState<Plan[]>(STUB_PLANS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Read cycle and message from URL params
  useEffect(() => {
    const cycleParam = getQueryParam("cycle");
    if (cycleParam === "monthly" || cycleParam === "yearly") {
      setBillingCycle(cycleParam);
    }
    
    // Show error message if subscription required
    const messageParam = getQueryParam("message");
    if (messageParam === "subscription_required") {
      setError("Please complete your subscription to access the trading platform. Select a plan below to continue.");
    }
  }, []);

  // Fetch plans if authenticated
  useEffect(() => {
    const fetchPlans = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await getPlans();
        
        // Map plan codes to display names
        const mappedPlans = response.plans.map((plan) => {
          let displayName = plan.name;
          if (plan.code === "STANDARD") {
            displayName = "Pro";
          } else if (plan.code === "PRO") {
            displayName = "Ultra";
          }
          return { ...plan, name: displayName };
        });

        setPlans(mappedPlans);
      } catch (err) {
        // If fetch fails, use stub data
        logger.error("Failed to fetch plans, using stub data", err instanceof Error ? err : new Error(String(err)));
        setPlans(STUB_PLANS);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchPlans();
  }, [isAuthenticated]);

  const handleCycleToggle = (cycle: BillingCycle) => {
    setBillingCycle(cycle);
    // Update URL without navigation
    const newUrl = preserveQueryParams(window.location.pathname, { cycle });
    window.history.replaceState({}, "", newUrl);
  };

  const handleStartClick = (planCode: string) => {
    const returnTo = preserveQueryParams("/subscribe", {
      plan: planCode,
      cycle: billingCycle,
    });
    window.location.href = `/auth/register?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const handleSubscribeClick = (planCode: string) => {
    const subscribeUrl = preserveQueryParams("/subscribe", {
      plan: planCode,
      cycle: billingCycle,
    });
    
    // If auth is still loading, assume not authenticated and redirect to register
    // This prevents showing the "Authentication Required" screen in SubscribePage
    if (authLoading || !isAuthenticated) {
      window.location.href = `/auth/register?returnTo=${encodeURIComponent(subscribeUrl)}`;
      return;
    }
    
    // If authenticated, go directly to subscribe page
    window.location.href = subscribeUrl;
  };

  const getPlanPrice = (plan: Plan): number | null => {
    return billingCycle === "monthly" ? plan.prices.monthly : plan.prices.yearly;
  };

  const formatPrice = (price: number | null): string => {
    if (price === null) {
      return "Contact us";
    }
    return `$${price.toFixed(2)}`;
  };

  // Filter to only STANDARD and PRO plans
  const displayedPlans = plans.filter(
    (plan) => plan.code === "STANDARD" || plan.code === "PRO"
  );

  // Sort: STANDARD first, then PRO
  displayedPlans.sort((a, b) => {
    if (a.code === "STANDARD") return -1;
    if (b.code === "STANDARD") return 1;
    return 0;
  });

  return (
    <PublicLayout>
      <div className="pricing-page">
        <div className="pricing-page__container">
          {/* Header */}
          <div className="pricing-page__header">
            <h1 className="pricing-page__title">Simple pricing for serious traders</h1>
            <p className="pricing-page__subtitle">
              Choose monthly or yearly. Cancel renewal anytime.
            </p>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="pricing-toggle">
            <button
              type="button"
              className={`pricing-toggle__button ${
                billingCycle === "monthly" ? "pricing-toggle__button--active" : ""
              }`}
              onClick={() => handleCycleToggle("monthly")}
              aria-pressed={billingCycle === "monthly"}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`pricing-toggle__button ${
                billingCycle === "yearly" ? "pricing-toggle__button--active" : ""
              }`}
              onClick={() => handleCycleToggle("yearly")}
              aria-pressed={billingCycle === "yearly"}
            >
              Yearly <span className="pricing-toggle__save">(15% discount)</span>
            </button>
          </div>

          {/* Discount Notice */}
          {billingCycle === "yearly" && (
            <div className="pricing-discount-notice">
              <p className="pricing-discount-notice__text">
                💰 <strong>Save 15%</strong> when you choose yearly billing! 
                Pay for 10 months, get 12 months of access.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="pricing-page__error" role="alert">
              {error}
            </div>
          )}

          {/* Plans Grid */}
          {isLoading ? (
            <div className="pricing-page__loading">Loading plans...</div>
          ) : (
            <div className="pricing-plans-grid">
              {displayedPlans.map((plan) => {
                const price = getPlanPrice(plan);
                const isPopular = plan.code === "STANDARD";

                return (
                  <div
                    key={plan.id}
                    className={`pricing-plan-card ${
                      isPopular ? "pricing-plan-card--popular" : ""
                    }`}
                  >
                    {isPopular && (
                      <div className="pricing-plan-card__badge">Most popular</div>
                    )}
                    <h3 className="pricing-plan-card__title">{plan.name}</h3>
                    <div className="pricing-plan-card__price">
                      {billingCycle === "yearly" && plan.prices.monthly && (
                        <div className="pricing-plan-card__price-comparison">
                          <span className="pricing-plan-card__price-original">
                            ${(plan.prices.monthly * 12).toFixed(2)}
                          </span>
                          <span className="pricing-plan-card__price-discount">
                            Save {((1 - (price! / (plan.prices.monthly * 12))) * 100).toFixed(0)}%
                          </span>
                        </div>
                      )}
                      <span className="pricing-plan-card__price-amount">
                        {formatPrice(price)}
                      </span>
                      {price !== null && (
                        <span className="pricing-plan-card__price-period">
                          /{billingCycle === "monthly" ? "month" : "year"}
                        </span>
                      )}
                    </div>
                    <p className="pricing-plan-card__tagline">
                      {plan.code === "STANDARD"
                        ? "Smart auto-trading with safety controls."
                        : "Everything in Pro + priority support & advanced insights."}
                    </p>
                    <ul className="pricing-plan-card__features">
                      {plan.code === "STANDARD" && (
                        <>
                          <li>AI trade signals + automated execution</li>
                          <li>Encrypted API-key storage</li>
                          <li>Core monitoring & safe exits</li>
                          <li>Subscription & payment tracking</li>
                          <li>Standard support</li>
                        </>
                      )}
                      {plan.code === "PRO" && (
                        <>
                          <li>Everything in Pro</li>
                          <li>Priority support</li>
                          <li>Advanced reporting & diagnostics</li>
                          <li>Faster issue handling</li>
                          <li>Early access to new features</li>
                        </>
                      )}
                    </ul>
                    <div className="pricing-plan-card__ctas">
                      <button
                        type="button"
                        className="pricing-plan-card__cta pricing-plan-card__cta--primary"
                        onClick={() => handleSubscribeClick(plan.code)}
                      >
                        Subscribe
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* All Plans Include */}
          <div className="pricing-includes">
            <h2 className="pricing-includes__title">All plans include</h2>
            <ul className="pricing-includes__list">
              <li>AI trade signals + automated execution</li>
              <li>Encrypted API-key storage</li>
              <li>Core monitoring & safe exits</li>
              <li>Subscription & payment tracking</li>
              <li>Standard support</li>
            </ul>
          </div>

          {/* Pricing Notes */}
          <div className="pricing-notes">
            <p className="pricing-notes__text">
              Automated trading currently executes on Bybit only. A Bybit account is required to trade.
            </p>
            <p className="pricing-notes__text">
              Payments are currently supported via USDT/USDC on Ethereum (ERC-20).
            </p>
            <p className="pricing-notes__text">
              Trading involves risk and may result in the loss of your invested capital. We do not guarantee profits. Past or
              backtested performance is not a guarantee of future results.
            </p>
          </div>

          {/* Pricing FAQ */}
          <div className="pricing-faq">
            <h2 className="pricing-faq__title">Pricing FAQ</h2>
            <div className="pricing-faq__list">
              <div className="pricing-faq-item">
                <h3 className="pricing-faq-item__question">
                  Do you guarantee profit?
                </h3>
                <p className="pricing-faq-item__answer">
                  No. Trading carries risk, and you may lose your invested capital. Past performance does not guarantee future results.
                </p>
              </div>
              <div className="pricing-faq-item">
                <h3 className="pricing-faq-item__question">Do you hold my funds?</h3>
                <p className="pricing-faq-item__answer">
                  No. Funds stay on your exchange.
                </p>
              </div>
              <div className="pricing-faq-item">
                <h3 className="pricing-faq-item__question">Can I cancel?</h3>
                <p className="pricing-faq-item__answer">
                  You can cancel renewal anytime; access continues until period end.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

export default PricingPage;

